/**
 * Typed access to data/tuning.json.
 *
 * RULE (see AGENTS.md): no gameplay number is written in a .ts file. If you
 * find yourself typing a literal like `4.2` or `0.35` into logic, it belongs in
 * tuning.json and here. The payoff is that the kids and the designer can retune
 * the entire game feel by editing one JSON file, no rebuild.
 *
 * The interface below is intentionally explicit rather than inferred, because it
 * is the contract another agent reads to discover what is tunable.
 */

import raw from '@data/tuning.json';

export interface SimTuning {
  fixedHz: number;
  maxCatchUpSteps: number;
  gravity: number;
}

export interface CatNoiseTuning {
  idle: number;
  crouch: number;
  walk: number;
  run: number;
  land: number;
  landHard: number;
  landMinSpeed: number;
}

export type InstinctCategoryName = 'prop' | 'key' | 'trace' | 'hideSpot' | 'goal';

/** Spelled out rather than importing HeatTier, which would make core/tuning
 *  depend on core/events. */
export type TierScale = { standby: number; suspicious: number; hunting: number; frenzy: number };

export type QuadrupedLegName = 'frontLeft' | 'frontRight' | 'hindLeft' | 'hindRight';
export type QuadrupedGaitName = 'idle' | 'walk' | 'trot' | 'run';
export type QuadrupedPhaseOffsets = [number, number, number, number];

export interface CatLocomotionTuning {
  gaitSpeedThresholds: { walk: number; trot: number; run: number };
  strideLength: number;
  strideHeight: number;
  strideScale: { walk: number; trot: number; run: number };
  stepFrequencyScale: number;
  stanceFraction: number;
  legPhaseOffsets: {
    walk: QuadrupedPhaseOffsets;
    trot: QuadrupedPhaseOffsets;
    run: QuadrupedPhaseOffsets;
  };
  spineLeanPitch: number;
  spineLeanRoll: number;
  spineLeanDamping: number;
  accelerationForFullLean: number;
  turnRateForFullLean: number;
  headLookMaxYaw: number;
  headLookMaxPitch: number;
  headLookDamping: number;
  tailJointCount: number;
  tailStiffness: number;
  tailDamping: number;
  tailVelocityInfluence: number;
  tailMaxBend: number;
  earStiffness: number;
  earDamping: number;
  earVelocityInfluence: number;
  earTurnInfluence: number;
  earMaxTilt: number;
  groundProbeHeight: number;
  skeleton: {
    legRootOffsets: Record<QuadrupedLegName, [number, number, number]>;
    legLength: number;
    bodyLength: number;
    bodyHeightScale: number;
    bodyLift: number;
    spinePivot: [number, number, number];
    headOffset: [number, number, number];
    headRadius: number;
    legThickness: number;
    pawRadius: number;
    tailBaseOffset: [number, number, number];
    tailJointLength: number;
    tailThickness: number;
    tailLift: number;
    earOffsets: [[number, number, number], [number, number, number]];
    earRadius: number;
    earHeight: number;
    eyeOffsets: [[number, number, number], [number, number, number]];
    eyeRadius: number;
  };
}

export interface CatRagdollTuning {
  torsoSize: [number, number, number];
  headRadius: number;
  legRadius: number;
  torsoMass: number;
  headMass: number;
  legSegmentMass: number;
  linearDamping: number;
  angularDamping: number;
  friction: number;
  restitution: number;
  fallSpeedThreshold: number;
  fallImpulseHorizontalScale: number;
  fallImpulseVerticalScale: number;
  sprayImpulseHorizontal: number;
  sprayImpulseUp: number;
  minDuration: number;
  maxDuration: number;
  settleSpeed: number;
  recoveryBlendSeconds: number;
  recoveryGroundProbeHeight: number;
  solverIterations: number;
  noise: number;
  cameraShake: { spray: number; fall: number };
}

export interface CatTuning {
  radius: number;
  height: number;
  mass: number;
  walkSpeed: number;
  runSpeed: number;
  crouchSpeed: number;
  acceleration: number;
  deceleration: number;
  airControl: number;
  turnRateDeg: number;
  jumpSpeed: number;
  coyoteTime: number;
  maxSlopeClimbDeg: number;
  minSlopeSlideDeg: number;
  stepHeight: number;
  snapToGroundDistance: number;
  dash: { speed: number; duration: number; cooldown: number; noise: number };
  pounce: {
    minChargeTime: number;
    maxChargeTime: number;
    minSpeed: number;
    maxSpeed: number;
    upwardRatio: number;
    noise: number;
  };
  carry: { speedMultiplier: number; dashBlocked: boolean };
  noise: CatNoiseTuning;
  locomotion: CatLocomotionTuning;
  ragdoll: CatRagdollTuning;
}

export interface GrannyTuning {
  radius: number;
  height: number;
  speeds: { routine: number; investigate: number; hunt: number; frenzy: number };
  turnRateDeg: number;
  presentation: {
    hearingSharpThreshold: number;
    hearingDullThreshold: number;
    chipMaxDistance: number;
    chipHeadClearance: number;
    face: {
      eyeSpacing: number;
      eyeHeight: number;
      eyeRadius: number;
      noseHeight: number;
      noseRadius: number;
      depth: number;
    };
  };
  vision: {
    fovDeg: number;
    range: number;
    personalSpaceRadius: number;
    eyeHeight: number;
    coneGroundIndicator: boolean;
    /** Heat per second while she can see the cat. Zero at standby. */
    sightHeatPerSecond: number;
    sightHeatTierScale: TierScale;
  };
  hearing: {
    threshold: number;
    baseRange: number;
    perWallAttenuation: number;
    maxWalls: number;
    movementHeatPerSecond: number;
    movementHeatTierScale: TierScale;
    /** Perceived loudness that pulls her off what she is doing, by tier. */
    investigateLoudness: TierScale;
  };
  contact: {
    fillRateClose: number;
    fillRateFar: number;
    closeDistance: number;
    crouchMultiplier: number;
    stillMultiplier: number;
    drainRate: number;
    hideSpotDrainMultiplier: number;
    searchLastKnownSeconds: number;
    spottedThreshold: number;
  };
  memory: {
    lastKnownDecaySeconds: number;
    hideSpotRecheckSeconds: number;
    noiseDecaySeconds: number;
  };
  /** Calm granny wandering over for a fuss. See DESIGN.md's Standby row. */
  pet: {
    cooldownSeconds: number;
    noticeRange: number;
    reachDistance: number;
    durationSeconds: number;
    giveUpSeconds: number;
    cozyPoints: number;
  };
  /** Walking the caught cat back to its bed. */
  carry: {
    pauseSeconds: number;
    speed: number;
    holdOffset: [number, number, number];
    placeRadius: number;
    timeoutSeconds: number;
    /** Metres of progress that count as "still getting there". */
    progressEpsilon: number;
    sidestepAfterSeconds: number;
    sidestepSeconds: number;
    sidestepDistance: number;
  };
  spray: { range: number; coneDeg: number; cooldown: number; windUpTime: number };
  catch: { range: number; requiresContact: number; windUpTime: number };
  frenzy: { staminaSeconds: number; wheezeSeconds: number };
  investigate: {
    arriveRadius: number;
    lookAroundSeconds: number;
    resumeRoutineDelay: number;
    suspiciousCheckSeconds: number;
    /** The "?" beat scans this arc, so her cone actually moves while she looks. */
    lookSweepArcDeg: number;
    lookSweepHz: number;
  };
  navigation: {
    routineTravelTimeoutSeconds: number;
    searchReachTimeoutSeconds: number;
    investigateTimeoutSeconds: number;
  };
}

export interface HeatTuning {
  max: number;
  tiers: {
    riseAt: { suspicious: number; hunting: number; frenzy: number };
    fallAt: { suspicious: number; hunting: number; frenzy: number };
    minDwellSeconds: number;
  };
  decay: {
    hiddenAndQuiet: number;
    merelyUnobserved: number;
    unresolvedTraceInRoom: number;
  };
  onCatch: { resetTo: number };
}

export interface QualityTierSettings {
  pixelRatioCap: number;
  shadows: boolean;
  shadowMapSize: number;
  outlines: 'charactersOnly' | 'all';
  maxDynamicProps: number;
  vfxDensity: number;
  antialias: boolean;
}

export interface PropsTuning {
  /** breakSpeed = manifest breakForce / this. See the note in tuning.json. */
  breakSpeedDivisor: number;
  /** Metres a prop must move from rest before it counts as "knocked". */
  knockedDisplacement: number;
  /** Speed below which a prop is considered settled. */
  restSpeed: number;
  /** A prop must exceed this speed for a landing to be resolved at all. */
  minFallSpeedToResolve: number;
  shardLifetimeSeconds: number;
  sleepAfterSeconds: number;
}

export interface Tuning {
  sim: SimTuning;
  cat: CatTuning;
  granny: GrannyTuning;
  props: PropsTuning;
  heat: HeatTuning;
  traces: { discoveryRadius: number; discoveryRequiresLos: boolean; resolveSeconds: number };
  instinct: {
    timeScale: number;
    desaturation: number;
    maxHighlightRange: number;
    /** Must stay 0. Kept so the "no cooldown" decision is explicit, not implied. */
    cooldown: number;
    pulseHz: number;
    markerSize: number;
    markerOpacity: number;
    markerPulseScale: number;
    markerLift: number;
    markerColors: Record<InstinctCategoryName, string>;
    /** An escape route deserves a bigger mark than a vase. */
    hideSpotMarkerScale: number;
    /** The spot you are already in shrinks: it is somewhere you ARE, not somewhere to go. */
    hideSpotOccupiedScale: number;
    /** Goals share the win-state gold with keys, so size is what tells them apart. */
    goalMarkerScale: number;
  };
  render: {
    outlineThickness: number;
    outlineThicknessCharacter: number;
    outlineColor: string;
    /** Brightness of each cel band, dark to light. Length sets the band count. */
    toonBands: number[];
  };
  camera: {
    distance: number;
    height: number;
    pitchDeg: number;
    fovDeg: number;
    followLag: number;
    lookAheadFactor: number;
    collisionPadding: number;
    shakeOnImpact: number;
    frenzyFovBoost: number;
  };
  score: {
    chaosPerBreakable: number;
    chaosPerTrace: number;
    cozyPerMoment: number;
    ghostBonus: number;
    hurricaneBonus: number;
    curiousCatBonus: number;
    stars: { one: number; two: number; three: number };
  };
  quality: {
    targetFpsDesktop: number;
    targetFpsMobile: number;
    downgradeAfterSlowFrames: number;
    tiers: { low: QualityTierSettings; medium: QualityTierSettings; high: QualityTierSettings };
  };
  editor: {
    gridStep: number;
    rotationStepDegrees: number;
    markerSize: number;
    markerColor: string;
  };
  budgets: {
    maxTrisCat: number;
    maxTrisGranny: number;
    maxTrisProp: number;
    maxTrisLevel: number;
    maxDrawCalls: number;
    maxTextureSize: number;
    maxMaterialFamilies: number;
  };
}

/** The `$comment` keys in the JSON are documentation for humans and agents; they
 *  are not part of the typed surface, hence the cast. */
export const tuning = raw as unknown as Tuning;

/** Derived once, used everywhere. */
export const FIXED_DT = 1 / tuning.sim.fixedHz;
